import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

/**
 * ModuleCard — diseño "vivo" con identidad cromática por módulo.
 *
 * - Borde 1.5px del color del módulo (visible pero no agresivo).
 * - Sombra inferior coloreada para dar profundidad y "elevación".
 * - Glow sutil en esquina superior derecha (~12% opacity).
 * - Gradiente muy ligero del color hacia el surface neutral.
 * - Icono y stat en color del módulo → cada tarjeta "respira" su familia.
 * - Hover: sombra y borde se intensifican.
 */
export default function ModuleCard({ module, index = 0, stat, layout = "bento" }) {
  const { title, description, color, route, Icon, span, key: k } = module;

  // Tonos derivados del color
  const tintBg = `${color}10`;     // 6% sobre superficie
  const borderColor = `${color}66`; // 40% — visible
  const borderHover = `${color}AA`; // 67% — más vivo
  const shadow = `0 12px 28px -14px ${color}55, 0 2px 4px -2px ${color}33`;
  const shadowHover = `0 18px 36px -14px ${color}88, 0 3px 8px -2px ${color}55`;

  /* ------------------------------------------------------------------ */
  /*  ROW LAYOUT                                                         */
  /* ------------------------------------------------------------------ */
  if (layout === "row") {
    return (
      <motion.div
        initial={{ opacity: 0, x: -12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.35, delay: index * 0.025, ease: [0.22, 1, 0.36, 1] }}
      >
        <Link
          to={route}
          data-testid={`module-card-${k}`}
          className="group relative flex items-center gap-4 sm:gap-5 pl-5 sm:pl-6 pr-4 sm:pr-5 py-4 sm:py-5 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-0.5"
          style={{
            background: `linear-gradient(135deg, ${tintBg} 0%, var(--legatum-surface) 60%)`,
            border: `1.5px solid ${borderColor}`,
            boxShadow: shadow,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = borderHover;
            e.currentTarget.style.boxShadow = shadowHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = borderColor;
            e.currentTarget.style.boxShadow = shadow;
          }}
        >
          {/* Acento lateral grueso */}
          <span
            aria-hidden
            className="absolute left-0 top-3 bottom-3 w-[4px] rounded-r-full"
            style={{ background: color, boxShadow: `0 0 10px ${color}77` }}
          />

          <div
            className="relative shrink-0 h-11 w-11 sm:h-12 sm:w-12 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-105"
            style={{
              background: `${color}1F`,
              border: `1px solid ${color}55`,
              boxShadow: `inset 0 1px 0 ${color}22`,
            }}
          >
            <Icon className="h-5 w-5" strokeWidth={2.2} style={{ color }} />
          </div>

          <div className="relative flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-semibold tracking-tight text-[var(--legatum-text)] leading-tight break-words">
              {title}
            </h3>
            <p className="mt-0.5 text-xs sm:text-sm text-[var(--legatum-text-secondary)] leading-snug break-words line-clamp-1 sm:line-clamp-2">
              {description}
            </p>
          </div>

          {stat && (
            <div className="relative hidden sm:flex flex-col items-end shrink-0 pl-4 sm:pl-6 border-l border-[var(--legatum-border)] min-w-[120px] md:min-w-[150px]">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-[var(--legatum-text-muted)] font-bold whitespace-nowrap">
                {stat.label}
              </div>
              <div
                className="mt-0.5 text-lg md:text-xl font-bold tracking-tight tabular-nums whitespace-nowrap"
                style={{ color }}
              >
                {stat.value}
              </div>
            </div>
          )}

          <div
            className="relative h-8 w-8 rounded-full flex items-center justify-center transition-all duration-300 shrink-0"
            style={{ background: `${color}1A`, border: `1px solid ${color}33` }}
          >
            <ArrowUpRight
              className="h-4 w-4 transition-colors"
              strokeWidth={2.2}
              style={{ color }}
            />
          </div>
        </Link>
      </motion.div>
    );
  }

  /* ------------------------------------------------------------------ */
  /*  BENTO CARD                                                         */
  /* ------------------------------------------------------------------ */
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.03, ease: [0.22, 1, 0.36, 1] }}
      className={`min-h-[176px] ${span}`}
    >
      <Link
        to={route}
        data-testid={`module-card-${k}`}
        className="group relative flex flex-col h-full p-5 md:p-6 pl-6 md:pl-7 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
        style={{
          background: `linear-gradient(135deg, ${tintBg} 0%, var(--legatum-surface) 55%)`,
          border: `1.5px solid ${borderColor}`,
          boxShadow: shadow,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = borderHover;
          e.currentTarget.style.boxShadow = shadowHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = borderColor;
          e.currentTarget.style.boxShadow = shadow;
        }}
      >
        {/* Acento lateral grueso con halo */}
        <span
          aria-hidden
          className="absolute left-0 top-5 bottom-5 w-[4px] rounded-r-full"
          style={{ background: color, boxShadow: `0 0 12px ${color}88` }}
        />

        {/* Glow en esquina superior derecha (color del módulo) */}
        <div
          className="absolute -top-12 -right-12 h-44 w-44 rounded-full pointer-events-none blur-3xl"
          style={{ background: color, opacity: 0.18 }}
        />

        {/* Patrón de puntos sutil sobre el glow */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(${color} 1px, transparent 1px)`,
            backgroundSize: "16px 16px",
          }}
        />

        <div className="relative flex items-start justify-between shrink-0">
          <div
            className="h-11 w-11 md:h-12 md:w-12 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-105"
            style={{
              background: `${color}1F`,
              border: `1px solid ${color}55`,
              boxShadow: `inset 0 1px 0 ${color}33, 0 4px 12px -6px ${color}66`,
            }}
          >
            <Icon className="h-5 w-5" strokeWidth={2.2} style={{ color }} />
          </div>
          <div
            className="h-8 w-8 rounded-full flex items-center justify-center transition-all duration-300 shrink-0"
            style={{ background: `${color}1A`, border: `1px solid ${color}33` }}
          >
            <ArrowUpRight className="h-4 w-4 transition-colors" strokeWidth={2.2} style={{ color }} />
          </div>
        </div>

        <div className="relative mt-5 md:mt-6 flex-1 min-w-0">
          <h3 className="text-base md:text-lg font-semibold tracking-tight text-[var(--legatum-text)] leading-tight break-words">
            {title}
          </h3>
          <p className="mt-1.5 text-xs md:text-sm text-[var(--legatum-text-secondary)] leading-snug break-words line-clamp-2">
            {description}
          </p>
        </div>

        {stat && (
          <div
            className="relative mt-4 pt-3 shrink-0"
            style={{ borderTop: `1px solid ${color}33` }}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--legatum-text-muted)] font-bold truncate">
              {stat.label}
            </div>
            <div
              className="mt-0.5 text-xl md:text-2xl font-bold tracking-tight tabular-nums truncate"
              style={{ color }}
            >
              {stat.value}
            </div>
          </div>
        )}
      </Link>
    </motion.div>
  );
}
